// "DE onboarding": the same guided onboarding for a digital employee (build order, increment 2).
// It runs on a DE hired onto a seat (status onboarding); hiring from a template prefills it (the
// template's KPIs are seeded on the seat at hire, its profile text is offered in the fields).
// Communication and responsibility follow the lanes of the org chart: the flow computes the
// escalation line and hand-off peers from the chart and asks a person to confirm them.
//
// The runtime controls are the runtime's own rows, edited in place: the seat's agent policy
// (least privilege: unset classes follow the company default), the DE's model map (its template
// override) and its job's home channel. Nothing here is a setting no one reads.
import type { FlowDef } from "./types";

export const DE_ONBOARDING: FlowDef = {
  key: "de_onboarding",
  title: "DE onboarding",
  callToAction: "Finish the DE's onboarding",
  chapters: [
    {
      key: "profile",
      title: "Role, goal & backstory",
      steps: [
        {
          key: "profile",
          title: "Role, goal & backstory",
          info: "What the DE is, in its own words.",
          see: ["manager"],
          fields: [
            {
              key: "role",
              label: "Role",
              info: "One or two lines: what it does for the company.",
              type: "longtext",
              maxLength: 2000,
              required: true,
              bind: { kind: "de_profile", column: "role" },
              fill: ["manager"],
            },
            {
              key: "goal",
              label: "Goal",
              info: "The outcome it works toward; its KPIs measure this.",
              type: "longtext",
              maxLength: 4000,
              required: true,
              bind: { kind: "de_profile", column: "goal" },
              fill: ["manager"],
            },
            {
              key: "backstory",
              label: "Backstory",
              info: "Context that shapes how it works: experience, tone, what it knows.",
              type: "longtext",
              maxLength: 8000,
              required: true,
              bind: { kind: "de_profile", column: "backstory" },
              fill: ["manager"],
            },
          ],
        },
      ],
    },
    {
      key: "seat",
      title: "Seat & reporting",
      steps: [
        {
          key: "seat",
          title: "Seat, reports to, position",
          info: "Where it sits. Its chain must end at a person.",
          see: ["manager"],
          fields: [
            {
              key: "title",
              label: "Seat title",
              info: "The seat's name on the chart.",
              type: "text",
              maxLength: 200,
              required: true,
              bind: { kind: "seat", column: "title" },
              fill: ["manager"],
            },
            {
              key: "reports_to",
              label: "Reports to",
              info: "A person's seat, or a DE's seat whose chain ends at a person.",
              type: "seat",
              required: true,
              bind: { kind: "seat", column: "supervisor_seat_id" },
              fill: ["manager"],
            },
            {
              key: "staff",
              label: "Staff position",
              info: "Staff: assists its supervisor, beside them. Line: in the row of reports.",
              type: "boolean",
              bind: { kind: "seat", column: "staff" },
              fill: ["manager"],
            },
          ],
        },
        {
          key: "lanes",
          title: "Escalation lanes",
          info: "Set from the chart. It escalates up its line and hands off to these peers only.",
          see: ["manager"],
          fields: [
            {
              key: "lanes",
              label: "Lanes",
              info: "Escalates to its supervisor's seat; hands off to the seats beside it and those its seat names.",
              type: "confirm",
              required: true,
              bind: { kind: "lanes" },
              fill: ["manager"],
            },
          ],
        },
      ],
    },
    {
      key: "duties",
      title: "Responsibilities & KPIs",
      steps: [
        {
          key: "duties",
          title: "Responsibilities & KPIs",
          info: "What it owns and how it is measured.",
          see: ["manager"],
          fields: [
            {
              key: "responsibilities",
              label: "Responsibilities",
              info: "The outcomes the seat owns. At least one.",
              type: "linked",
              required: true,
              bind: { kind: "linked", editor: "responsibilities" },
              fill: ["manager"],
            },
            {
              key: "kpis",
              label: "KPIs",
              info: "A template brings its KPIs; adjust targets here.",
              type: "linked",
              required: true,
              bind: { kind: "linked", editor: "kpis" },
              fill: ["manager"],
            },
          ],
        },
      ],
    },
    {
      key: "access",
      title: "Tools & permissions",
      steps: [
        {
          key: "access",
          title: "Tools & permissions",
          info: "Least privilege: start with nothing, add what the job needs.",
          see: ["manager"],
          fields: [
            {
              key: "agent_policy",
              label: "Permissions",
              info: "What it may do alone, ask first, or never. Unset classes follow the company default.",
              type: "linked",
              bind: { kind: "linked", editor: "agent_policy" },
              fill: ["manager"],
            },
            {
              key: "post_approval",
              label: "Posts need approval",
              info: "A person approves each chat post before it goes out.",
              type: "boolean",
              required: true,
              bind: { kind: "post_approval" },
              fill: ["manager"],
            },
          ],
        },
      ],
    },
    {
      key: "operations",
      title: "Home, budget & models",
      steps: [
        {
          key: "operations",
          title: "Home channel, budget, models",
          info: "Where it works, what it may spend, which models it runs on.",
          see: ["manager"],
          fields: [
            {
              key: "home_channel",
              label: "Home channel",
              info: "The chat channel it reports in and is reached from (its job's home).",
              type: "linked",
              bind: { kind: "linked", editor: "home_channel" },
              fill: ["manager"],
            },
            {
              key: "budget",
              label: "Budget",
              info: "Its monthly spend cap; runs stop at the cap.",
              type: "linked",
              required: true,
              bind: { kind: "linked", editor: "budget" },
              fill: ["manager"],
            },
            {
              key: "models",
              label: "Models",
              info: "The model for each of its roles. Unset roles use the template's.",
              type: "linked",
              bind: { kind: "linked", editor: "models" },
              fill: ["manager"],
            },
          ],
        },
      ],
    },
    {
      key: "how",
      title: "How I work",
      steps: [
        {
          key: "how",
          title: "How I work",
          info: "Its process in plain words: steps, when it asks a person, what it never does.",
          see: ["manager"],
          fields: [
            {
              key: "how_i_work",
              label: "How I work",
              info: "Sent to the DE at its next run start.",
              type: "linked",
              required: true,
              bind: { kind: "linked", editor: "how_i_work" },
              fill: ["manager"],
            },
          ],
        },
      ],
    },
  ],
};

/**
 * What a template brings to the flow's text fields (shown prefilled, saved only when the person
 * saves the step). The template's KPIs are seeded on the seat at hire, so they need no prefill.
 */
export const DE_TEMPLATE_PREFILL: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  recruiting_de: {
    role: "Sources, screens and schedules candidates for open roles.",
    goal: "Fill open roles with qualified hires on time.",
  },
  sales_de: {
    role: "Qualifies inbound leads and books discovery calls.",
    goal: "Turn inbound interest into booked, qualified calls.",
  },
  support_de: {
    role: "Answers customer questions and routes what it cannot solve.",
    goal: "Resolve customer questions quickly and correctly.",
  },
};
